import { Link, useRouterState } from "@tanstack/react-router";
import { detectCluster, getClusterTheme, type Cluster } from "@/lib/cluster";
import { useLocale } from "@/i18n/LocaleContext";
import { SITE_ORIGIN } from "@/lib/seo/schema";

type Crumb = { label: string; to?: string };

const CLUSTER_ROOT: Record<Exclude<Cluster, null>, { id: { label: string; to: string }; en: { label: string; to: string } }> = {
  calendar: { id: { label: "Kalender", to: "/kalender" }, en: { label: "Calendar", to: "/en/calendar" } },
  hijri: { id: { label: "Hijriah & Puasa", to: "/kalender-hijriah" }, en: { label: "Hijri & Fasting", to: "/en/hijri-calendar" } },
  prayer: { id: { label: "Jadwal Sholat", to: "/jadwal-sholat" }, en: { label: "Prayer Schedule", to: "/jadwal-sholat" } },
  tools: { id: { label: "Tools", to: "/tools" }, en: { label: "Tools", to: "/en/tools" } },
  important: { id: { label: "Hari Penting & Zodiak", to: "/hari" }, en: { label: "Notable Days & Zodiac", to: "/hari" } },
};

const SEG_LABELS: Record<string, string> = {
  "kalender": "Kalender",
  "calendar": "Calendar",
  "kalender-hijriah": "Hijriah",
  "hijri-calendar": "Hijri",
  "jadwal-sholat": "Jadwal Sholat",
  "jadwal-imsak": "Imsakiyah",
  "jadwal-buka-puasa": "Buka Puasa",
  "tanggal-merah": "Tanggal Merah",
  "red-dates": "Red Dates",
  "libur-nasional": "Libur Nasional",
  "public-holidays": "Public Holidays",
  "cuti-bersama": "Cuti Bersama",
  "joint-leave": "Joint Leave",
  "hari-ini": "Hari Ini",
  "today": "Today",
  "tools": "Tools",
  "kalkulator": "Kalkulator",
  "kalkulator-hari": "Kalkulator Hari",
  "cek-weton": "Cek Weton",
  "hari-jadian": "Hari Jadian",
  "hari-lahir": "Hari Lahir",
  "hari-menikah": "Lama Menikah",
  "hari-putus": "Hari Putus",
  "hari-sahabat": "Lama Bersahabat",
  "hari-ldr": "Lama LDR",
  "hari": "Hari Penting",
  "harpitnas": "Harpitnas",
  "zodiak": "Zodiak",
  "cocok": "Kecocokan",
  "lokasi": "Indeks Lokasi",
};

function pretty(seg: string): string {
  if (SEG_LABELS[seg]) return SEG_LABELS[seg];
  // Title-case slug
  return seg
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ClusterBreadcrumb() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const cluster = detectCluster(pathname);
  const { locale } = useLocale();
  if (!cluster) return null;
  if (pathname === "/" || pathname === "/en" || pathname === "/en/") return null;
  const theme = getClusterTheme(cluster);

  // Strip /en prefix for segment processing but remember the locale prefix.
  const isEn = pathname.startsWith("/en/") || pathname === "/en";
  const localePrefix = isEn ? "/en" : "";
  const cleanPath = isEn ? pathname.replace(/^\/en/, "") : pathname;
  const segs = cleanPath.split("/").filter(Boolean);
  if (segs.length === 0) return null;

  const home: Crumb = { label: locale === "en" ? "Home" : "Beranda", to: isEn ? "/en/" : "/" };
  const root = CLUSTER_ROOT[cluster][locale === "en" ? "en" : "id"];
  const rootCrumb: Crumb = { label: root.label, to: localePrefix + root.to.replace(/^\/en/, "") };

  // Build crumbs from segments after the cluster root segment.
  const crumbs: Crumb[] = [home, rootCrumb];
  let acc = localePrefix;
  for (let i = 0; i < segs.length; i++) {
    acc += "/" + segs[i];
    // Skip the cluster root itself (already added as rootCrumb).
    if (i === 0) continue;
    const isLast = i === segs.length - 1;
    crumbs.push({ label: pretty(segs[i]), to: isLast ? undefined : acc });
  }

  // JSON-LD
  const itemListElement = crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.label,
    ...(c.to ? { item: `${SITE_ORIGIN}${c.to}` } : {}),
  }));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };

  return (
    <>
      <nav
        aria-label={locale === "en" ? "Breadcrumb" : "Breadcrumb"}
        className="border-b border-border/60 bg-background"
      >
        <ol className="container mx-auto flex flex-wrap items-center gap-1 overflow-x-auto px-3 py-2 text-xs text-muted-foreground sm:px-4">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <li key={i} className="flex shrink-0 items-center gap-1">
                {c.to && !isLast ? (
                  <Link to={c.to as never} className={`rounded px-1 ${theme.hover}`}>
                    {c.label}
                  </Link>
                ) : (
                  <span className={isLast ? `font-medium ${theme.accentText}` : ""} aria-current={isLast ? "page" : undefined}>
                    {c.label}
                  </span>
                )}
                {!isLast && <span className="text-muted-foreground/60">/</span>}
              </li>
            );
          })}
        </ol>
      </nav>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
}
